import { ssrTrack } from '@/lib/actions/analytics/ssr-track';
import { WebAdminEmails } from '@/lib/auth/admins';
import { generateSecretCodeAction } from '@/lib/auth/secretCodeAction';
import {
    Avatar,
    Menu,
    Text,
    useComputedColorScheme,
    useMantineColorScheme,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import {
    IconLogin,
    IconLogout,
    IconMoon,
    IconSun,
    IconTrash,
} from '@tabler/icons-react';
import { Session } from 'next-auth';
import { signOut } from 'next-auth/react';
import { useResetInputs } from '../hooks/useResetInputs';

const ICON_SIZE_PX = 14;

export const AvatarMenu: React.FC<{ session: Session }> = ({ session }) => {
    const isWebAdmin = WebAdminEmails.has(session.user?.email ?? '');
    const { setColorScheme } = useMantineColorScheme();
    const computedColorScheme = useComputedColorScheme('light', {
        getInitialValueInEffect: true,
    });

    const resetInputs = useResetInputs();

    return (
        <Menu shadow="md" width={200} position="left-start">
            <Menu.Target>
                <Avatar
                    style={{ cursor: 'pointer', marginRight: '16px' }}
                    src={session.user?.image}
                    radius="xl"
                />
            </Menu.Target>

            <Menu.Dropdown>
                <div style={{ flex: 1, padding: '5px' }}>
                    <Text size="sm" fw={500}>
                        {session.user?.name}
                    </Text>

                    <Text c="gray" size="xs">
                        {session.user?.email}
                    </Text>
                </div>

                <Menu.Divider />

                <Menu.Item
                    onClick={() => resetInputs()}
                    leftSection={<IconTrash size={ICON_SIZE_PX} />}
                >
                    Reset all inputs
                </Menu.Item>

                {isWebAdmin && (
                    <>
                        <Menu.Item
                            onClick={async () => {
                                const response =
                                    await generateSecretCodeAction();

                                if ('error' in response) {
                                    notifications.show({
                                        message: `Error: ${response.error}`,
                                    });
                                    return;
                                }

                                const { code } = response;

                                console.log(`Get login code: ${code}`);

                                await navigator.clipboard.writeText(code);

                                notifications.show({
                                    message: `Copied login code ${code.slice(0, 6)}... to clipboard`,
                                });
                            }}
                            leftSection={<IconLogin size={ICON_SIZE_PX} />}
                        >
                            Copy login code
                        </Menu.Item>
                        <Menu.Item
                            onClick={async () => {
                                const response =
                                    await generateSecretCodeAction();

                                if ('error' in response) {
                                    notifications.show({
                                        message: `Error: ${response.error}`,
                                    });
                                    return;
                                }

                                const { code } = response;
                                console.log(`Login using secret code ${code}`);

                                await signOut({
                                    redirectTo: `/auto-login?code=${code}`,
                                });
                            }}
                            leftSection={<IconLogin size={ICON_SIZE_PX} />}
                        >
                            Login with code
                        </Menu.Item>
                        <Menu.Item
                            onClick={() => {
                                const newScheme =
                                    computedColorScheme === 'light'
                                        ? 'dark'
                                        : 'light';
                                setColorScheme(newScheme);
                                ssrTrack('setColorScheme', { newScheme });
                            }}
                            color="blue"
                            leftSection={
                                computedColorScheme === 'dark' ? (
                                    <IconSun size={ICON_SIZE_PX} />
                                ) : (
                                    <IconMoon size={ICON_SIZE_PX} />
                                )
                            }
                        >
                            {computedColorScheme === 'light' ? 'Dark' : 'Light'}{' '}
                            Mode
                        </Menu.Item>
                    </>
                )}

                <Menu.Item
                    onClick={() => {
                        window.localStorage.removeItem('signedIn');
                        signOut();
                        ssrTrack('signOut');
                    }}
                    color="red"
                    leftSection={<IconLogout size={ICON_SIZE_PX} />}
                >
                    Sign out
                </Menu.Item>
            </Menu.Dropdown>
        </Menu>
    );
};
